import { Component, inject, Input, OnDestroy, Output } from "@angular/core";
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { FgdsProductType, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { FgdsProject as Project } from "@domain/project/fgds-project";
import { untilDestroyed } from "@odx/angular/utils";
import { GasWarningCenterCardComponent } from "@project/gas-warning-center-configuration/gas-warning-center-card/gas-warning-center-card.component";
import { GasWarningCenterType } from "@project/gas-warning-center-configuration/gas-warning-center-type";
import { BehaviorSubject, combineLatest, defer, map, of, ReplaySubject } from "rxjs";

@Component({
  selector: "app-gas-warning-center-selector",
  templateUrl: "./gas-warning-center-selector.component.html",
  styleUrls: ["./gas-warning-center-selector.component.scss"],
  imports: [
    UiKitModule,
    ReactiveFormsModule,
    FilterProductComponent,
    RefreshProductsHintComponent,
    GasWarningCenterCardComponent,
  ],
  standalone: true,
})
export class GasWarningCenterSelectorComponent implements OnDestroy {
  private productService = inject(ProductService);
  private formBuilder = inject(FormBuilder);

  protected preselected?: Product;

  @Input({ required: true }) project!: Project;

  @Input({ required: true }) set type(type: GasWarningCenterType | undefined) {
    this.gasWarningCenterType = type;
    this.gasWarningCenterForm.reset();
    if (this.gasWarningCenterType) {
      this.collectGasWarningCenters(this.gasWarningCenterType);
    }
  }

  @Input() set originalGasWarningCenter(originalGasWarningCenter: Product | undefined) {
    if (!originalGasWarningCenter) {
      return;
    }
    this.preselected = originalGasWarningCenter;
  }

  @Output() gasWarningCenterSelect$ = defer(() =>
    this.gasWarningCenterForm.valueChanges.pipe(
      this.takeUntilDestroyed(),
      map((formValue) => formValue.selectedGasWarningCenter!),
    ),
  );

  protected gasWarningCenterForm = this.formBuilder.group({
    selectedGasWarningCenter: new FormControl<null | Product>(null, Validators.required),
  });

  @Output()
  formReady = of(this.gasWarningCenterForm);

  protected gasWarningCenterType: GasWarningCenterType | undefined;
  protected readonly filteredGasWarningCenters$ = new ReplaySubject<Product[]>(1);

  private readonly gasWarningCenters$ = new ReplaySubject<Product[]>(1);
  private readonly takeUntilDestroyed = untilDestroyed();
  private readonly searchValue$ = new BehaviorSubject<string | null>(null);

  constructor() {
    combineLatest([this.searchValue$, this.gasWarningCenters$]).subscribe(([searchValue, gasWarningCenters]) => {
      if (searchValue) {
        gasWarningCenters = gasWarningCenters.filter((product) => this.productService.searchFilter(product, searchValue));
      }
      this.filteredGasWarningCenters$.next(gasWarningCenters);
    });
  }

  ngOnDestroy() {
    this.gasWarningCenters$.complete();
    this.filteredGasWarningCenters$.complete();
    this.searchValue$.complete();
  }

  protected search(value: string) {
    this.searchValue$.next(value);
  }

  protected select(gasWarningCenter: Product, quantity: number) {
    if (!quantity) {
      return;
    }
    this.gasWarningCenterForm.patchValue({
      selectedGasWarningCenter: gasWarningCenter,
    });
  }

  private collectGasWarningCenters(type: GasWarningCenterType) {
    this.productService
      .getAvailableProducts(FgdsProductType.GASWARNINGCENTER)
      .pipe(
        map((products) => {
          if (type === null) {
            return products;
          }
          return products.filter((product) => type.isMatchingProduct(product.getLocalizedName("de-DE")));
        }),
      )
      .subscribe((gasWarningCenters) => this.gasWarningCenters$.next(gasWarningCenters));
  }
}
